import type { ClerkProviderProps } from "@clerk/clerk-react";
import { buildClerkThemeVariables } from "@ibada/shared-types/theme";
import { useEffect, useMemo, useState } from "react";

import { THEME_STORAGE_KEY } from "./storage";

type Appearance = NonNullable<ClerkProviderProps["appearance"]>;

type ResolvedColors = {
  isDark: boolean;
  primary: string;
  radius: number;
  /** Surface colors read from foundation.css so Clerk mirrors the app's single source of truth. */
  background?: string;
  inputBackground?: string;
};

function readFromDom(): ResolvedColors | null {
  if (typeof document === "undefined") return null;
  const root = document.documentElement;
  const primaryInline = root.style.getPropertyValue("--primary");
  if (!primaryInline) return null;
  const style = getComputedStyle(root);
  const isDark = root.classList.contains("dark");
  const primary = primaryInline.trim() || "#059669";
  const radiusStr = style.getPropertyValue("--radius").trim();
  const radius = radiusStr ? parseFloat(radiusStr) : 0.4;
  // Clerk cards/popovers sit on the elevated surface (--popover); inputs use --input. Reading them from the
  // live theme keeps Clerk in lockstep with foundation.css instead of a second hardcoded palette.
  const background = style.getPropertyValue("--popover").trim() || undefined;
  const inputBackground = style.getPropertyValue("--input").trim() || undefined;
  return { isDark, primary, radius, background, inputBackground };
}

function readFromStorage(): ResolvedColors {
  const fallbackIsDark =
    typeof window !== "undefined" ? window.matchMedia("(prefers-color-scheme: dark)").matches : false;

  if (typeof window === "undefined") {
    return { isDark: true, primary: "#059669", radius: 0.4 };
  }

  try {
    const raw = window.localStorage.getItem(THEME_STORAGE_KEY);
    if (!raw) {
      return { isDark: true, primary: "#059669", radius: 0.4 };
    }
    const config = JSON.parse(raw) as Record<string, unknown>;
    const mode = config.mode;
    const primary = typeof config.primary === "string" ? config.primary : "#059669";
    const radius = typeof config.radius === "number" ? config.radius : 0.4;
    let isDark: boolean;
    if (mode === "dark") isDark = true;
    else if (mode === "light") isDark = false;
    else isDark = fallbackIsDark;
    return { isDark, primary, radius };
  } catch {
    return { isDark: true, primary: "#059669", radius: 0.4 };
  }
}

// The app's one corner rule (foundation.css): containers take the soft surface radius, controls are pills.
// Clerk derives every corner from one `borderRadius` (its cards double it), so the variable keeps the menu-row
// radius our own menus use and each container and control is set here.
const CLERK_ROW_RADIUS = "0.375rem";
const CLERK_SURFACE = "!rounded-[var(--surface-radius)]";
const CLERK_PILL = "!rounded-full";

function buildAppearance({ isDark, primary, radius, background, inputBackground }: ResolvedColors): Appearance {
  return {
    variables: {
      ...buildClerkThemeVariables({ isDark, primary, radius, background, inputBackground }),
      borderRadius: CLERK_ROW_RADIUS,
    },
    elements: {
      card: `!px-4 sm:!px-5 ${CLERK_SURFACE}`,
      cardBox: CLERK_SURFACE,
      modalContent: CLERK_SURFACE,
      popoverBox: CLERK_SURFACE,
      userButtonPopoverCard: CLERK_SURFACE,
      userButtonPopoverMain: CLERK_SURFACE,
      scrollBox: CLERK_SURFACE,
      formButtonPrimary: CLERK_PILL,
      formButtonReset: CLERK_PILL,
      socialButtonsBlockButton: CLERK_PILL,
      socialButtonsIconButton: CLERK_PILL,
      alternativeMethodsBlockButton: CLERK_PILL,
      formFieldInput: CLERK_PILL,
      phoneInputBox: CLERK_PILL,
      selectButton: CLERK_PILL,
      formFieldInputShowPasswordButton: CLERK_PILL,
      identityPreview: CLERK_PILL,
      identityPreviewEditButton: CLERK_PILL,
      otpCodeFieldInput: CLERK_PILL,
      badge: CLERK_PILL,
      navbarButton: CLERK_PILL,
      profileSectionPrimaryButton: CLERK_PILL,
      menuButton: CLERK_PILL,
      modalCloseButton: CLERK_PILL,
      navbarMobileMenuButton: CLERK_PILL,
    },
  };
}

export function useClerkAppearance(): Appearance {
  const [colors, setColors] = useState<ResolvedColors>(() => readFromDom() ?? readFromStorage());

  useEffect(() => {
    // Re-read immediately — ThemeProvider may have run applyTheme by now
    setColors(readFromDom() ?? readFromStorage());

    const observer = new MutationObserver(() => {
      setColors(readFromDom() ?? readFromStorage());
    });

    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["class", "style"],
    });

    return () => observer.disconnect();
  }, []);

  return useMemo(() => buildAppearance(colors), [colors]);
}
